/**
 * @file useBookmarkedResumes.tsx
 *
 * @fileoverview Exports hook that stores bookmarked resumes in state,
 * and exposes a function to update the state.
 */
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { AppContext } from '~/jobiqo/context/app';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { UserContext } from '~/jobiqo/context/user';
import { useLazyQuery } from '@apollo/client';
import { Resume } from '~/jobiqo/types/types';
import { ErrorMessage } from '~/jobiqo/components/ContentErrorList';
import { getBookmarkedResumeQuery } from '~/jobiqo/components/BookmarkedResumes/graphql/queries/getBookmarkedResumes';

export interface UseBookmarkedResumesInterface {
  /**
   * The bookmarked resumes.
   */
  bookmarkedResumes: Resume[];
  /**
   * The callback to update bookmarkedResumes.
   */
  setBookmarkedResumes: Dispatch<SetStateAction<Resume[]>>;
  /**
   * The flag if graphql error happens.
   */
  error: boolean;
  /**
   * The list of errors received from the API.
   */
  errors: ErrorMessage[];
  /**
   * The flag indicating that query is loading.
   */
  loading: boolean;
  /**
   * The total amount of bookmarked resumes.
   */
  total: number;
}

export function useBookmarkedResumes(
  { itemsPerPage, page },
  initialState,
): UseBookmarkedResumesInterface {
  const { locale } = useContext(AppContext);
  const { RESUME, JOBS } = useFrontendConfig();
  const { user } = useContext(UserContext);
  const [bookmarkedResumes, setBookmarkedResumes] = useState<Resume[]>(
    initialState ?? [],
  );
  const [queryBookmarkedResumeQuery, { data, error, loading }] = useLazyQuery(
    getBookmarkedResumeQuery(user?.activeOrganization?.id, {
      RESUME_BADGES_ACTIVE: RESUME.RESUME_BADGES_ACTIVE,
      FIELDS_OF_STUDY_FEATURE_ACTIVE: RESUME.FIELDS_OF_STUDY_FEATURE_ACTIVE,
      SEASONAL_JOBS_ENABLED: JOBS.SEASONAL_JOBS_ENABLED,
    }),
  );

  useEffect(() => {
    // If the initialState is set, do not run the query when component mounts.
    if (user?.id && locale && !initialState) {
      queryBookmarkedResumeQuery({
        variables: {
          limit: itemsPerPage,
          locale,
          recruiterId: user.id,
          offset: (page > 0 ? page - 1 : 0) * itemsPerPage,
        },
        fetchPolicy: 'no-cache',
      }).then(({ data }) => {
        if (
          data?.bookmarkedResumes.resumes &&
          data.bookmarkedResumes.errors.length === 0
        ) {
          setBookmarkedResumes(data.bookmarkedResumes.resumes);
        }
      });
    }
  }, [user?.id, locale, page, initialState]);

  return {
    bookmarkedResumes,
    setBookmarkedResumes,
    error: !!error,
    loading,
    errors: data?.bookmarkedResumes.errors ?? [],
    total: data?.bookmarkedResumes.total ?? bookmarkedResumes.length,
  };
}
